import * as React from 'react';
import { Alert } from 'antd';
import { Tag } from '@frontend/app/refresh-components';
import { TooltipArrow, TooltipBody, TooltipHeader, TooltipItem, TooltipItemKey, TooltipItemValue, TooltipTitle } from '@frontend/applications/Social/components/tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Button } from '@revfluence/fresh';
import { DateRangeOptions, IDateRangeSettings } from '@frontend/app/components';
import { BoxArchiveIcon } from '@revfluence/fresh-icons/regular';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import AlertBar from '../../pages/Dashboard/LandingPageNavigation';
import { formatArchivedMessage } from '../../utils';
import { GetOfferById_offer } from '../../queries/types/GetOfferById';
import { GetAllBoostData_boostDataForAllAffiliatesInOffer } from '../../queries/types/GetAllBoostData';

interface FiltersAndAlertsProps {
  dateRangeSettings: IDateRangeSettings;
  selectedPayoutIds: number[];
  setSelectedPayoutIds: (ids: number[]) => void;
  selectedLastPayoutIds?: number[];
  setSelectedLastPayoutIds?: (ids: number[]) => void;
  numericFilters: Record<string, string[]>;
  setNumericFilters: (filters: Record<string, string[]>) => void;
  selectedStatusFilters: string[];
  setSelectedStatusFilters: (filters: string[]) => void;
  selectedBoostTypes: string[];
  setSelectedBoostTypes: (types: string[]) => void;
  selectedBoostStatuses: string[];
  setSelectedBoostStatuses: (statuses: string[]) => void;
  setIsMemberTableFilterOpen: (open: boolean) => void;
  offerDetails: GetOfferById_offer;
  shopifyLandingPage: boolean;
  linkForPromo: boolean;
  isLandingPageEnabled: boolean;
  offerId: number;
  baseUri: string;
  isOfferExpired: boolean;
  archivedDate: string | null;
  archivedUserName: string | null;
  landingPageUrl?: string;
  migrateToGraphQL: boolean;
  isOldOffer: boolean;
  setShowMigrationModal: (show: boolean) => void;
  boostData: GetAllBoostData_boostDataForAllAffiliatesInOffer[];
}

export const FiltersAndAlerts: React.FC<FiltersAndAlertsProps> = ({
  dateRangeSettings,
  selectedPayoutIds,
  setSelectedPayoutIds,
  selectedLastPayoutIds = [],
  setSelectedLastPayoutIds = () => { },
  numericFilters,
  setNumericFilters,
  selectedStatusFilters,
  setSelectedStatusFilters,
  selectedBoostTypes,
  setSelectedBoostTypes,
  selectedBoostStatuses,
  setSelectedBoostStatuses,
  setIsMemberTableFilterOpen,
  offerDetails,
  shopifyLandingPage,
  linkForPromo,
  isLandingPageEnabled,
  offerId,
  baseUri,
  isOfferExpired,
  archivedDate,
  archivedUserName,
  landingPageUrl,
  migrateToGraphQL,
  isOldOffer,
  setShowMigrationModal,
  boostData,
}) => {
  const { boostData: contextBoostData } = useOfferDetailsContext() || {};
  const effectiveBoostData = contextBoostData || boostData || [];
  return (
    <>
      {/* Landing Page Alert */}
      {shopifyLandingPage && linkForPromo && isLandingPageEnabled && (
        <AlertBar baseUri={baseUri} offerId={offerId} landingPageUrl={landingPageUrl} isOfferExpired={isOfferExpired} />
      )}
      {/* Filters */}
      <div className="flex gap-2 px-4 pt-4 flex-wrap">
        {/* Date Range Filter */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Tag
                label={dateRangeSettings.selectedOption === DateRangeOptions.CUSTOM
                  ? `${dateRangeSettings.dateRange?.startDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${dateRangeSettings.dateRange?.endDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                  : dateRangeSettings.selectedOption}
                bgColor="bg-grey-2"
                textColor="text-grey-6"
                onRemove={() => {
                  dateRangeSettings.setEarliestDate(null);
                  dateRangeSettings.onChange(DateRangeOptions.ALL_TIME);
                }}
                isRemovable={dateRangeSettings.selectedOption !== DateRangeOptions.ALL_TIME}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
            <TooltipHeader>
              <TooltipTitle>Date Range Filter</TooltipTitle>
            </TooltipHeader>
            <TooltipBody>
              <TooltipItem>
                <TooltipItemKey>Selected Date Range</TooltipItemKey>
                <TooltipItemValue>
                  {dateRangeSettings.selectedOption === DateRangeOptions.CUSTOM
                    ? `${dateRangeSettings.dateRange?.startDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${dateRangeSettings.dateRange?.endDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                    : dateRangeSettings.selectedOption}
                </TooltipItemValue>
              </TooltipItem>
            </TooltipBody>
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>
        {/* Payout Filter */}
        {selectedPayoutIds.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Tag
                  label={(() => {
                    if (selectedPayoutIds.includes(-1)) {
                      const selectedPayouts = offerDetails?.payouts?.filter((payout) => selectedPayoutIds.includes(payout.id)) || [];
                      const remainingCount = selectedPayouts.length;
                      return `Automated Tiers${remainingCount > 0 ? ` +${remainingCount}` : ''}`;
                    }
                    const selectedPayouts = offerDetails?.payouts?.filter((payout) => selectedPayoutIds.includes(payout.id)) || [];
                    if (selectedPayouts.length === 0) return 'No payouts selected';
                    const firstPayout = selectedPayouts[0];
                    const remainingCount = selectedPayouts.length - 1;
                    return `${firstPayout.label}${firstPayout.flatPayout && firstPayout.percentPayout
                      ? ` ($${firstPayout.flatPayout} + ${firstPayout.percentPayout}%)`
                      : firstPayout.flatPayout
                        ? ` ($${firstPayout.flatPayout})`
                        : firstPayout.percentPayout
                          ? ` (${firstPayout.percentPayout}%)`
                          : ''}${remainingCount > 0 ? ` +${remainingCount}` : ''}`;
                  })()}
                  bgColor="bg-grey-2"
                  textColor="text-grey-6"
                  onRemove={() => setSelectedPayoutIds([])}
                  isRemovable
                  className="cursor-pointer"
                  onClick={() => setIsMemberTableFilterOpen(true)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
              <TooltipHeader>
                <TooltipTitle>Payout Filter</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemKey>Selected Payouts</TooltipItemKey>
                  <TooltipItemValue>
                    {selectedPayoutIds.includes(-1) && (
                      <div className="mb-1">Automated Tiers</div>
                    )}
                    {offerDetails?.payouts
                      ?.filter((payout) => selectedPayoutIds.includes(payout.id))
                      .map((payout) => (
                        <div key={payout.id} className="mb-1">
                          {payout.label} {payout.offerPayoutRules?.length && payout.offerPayoutRules?.length > 1 ? '(Multiple)' : ''}
                          {payout.flatPayout && payout.percentPayout
                            ? ` ($${payout.flatPayout} + ${payout.percentPayout}%)`
                            : payout.flatPayout
                              ? ` ($${payout.flatPayout})`
                              : payout.percentPayout
                                ? ` (${payout.percentPayout}%)`
                                : ''}
                        </div>
                      ))}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
        {/* Last Payout Filter */}
        {selectedLastPayoutIds.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Tag
                  label={(() => {
                    const selectedPayouts = offerDetails?.payouts?.filter((payout) => selectedLastPayoutIds.includes(payout.id)) || [];
                    if (selectedPayouts.length === 0) return 'No last payouts selected';
                    const firstPayout = selectedPayouts[0];
                    const remainingCount = selectedPayouts.length - 1;
                    return `${firstPayout.label}${firstPayout.flatPayout && firstPayout.percentPayout
                      ? ` ($${firstPayout.flatPayout} + ${firstPayout.percentPayout}%)`
                      : firstPayout.flatPayout
                        ? ` ($${firstPayout.flatPayout})`
                        : firstPayout.percentPayout
                          ? ` (${firstPayout.percentPayout}%)`
                          : ''}${remainingCount > 0 ? ` +${remainingCount}` : ''}`;
                  })()}
                  bgColor="bg-grey-2"
                  textColor="text-grey-6"
                  onRemove={() => setSelectedLastPayoutIds([])}
                  isRemovable
                  className="cursor-pointer"
                  onClick={() => setIsMemberTableFilterOpen(true)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
              <TooltipHeader>
                <TooltipTitle>Last Payout Filter</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemKey>Selected Last Payouts</TooltipItemKey>
                  <TooltipItemValue>
                    {offerDetails?.payouts
                      ?.filter((payout) => selectedLastPayoutIds.includes(payout.id))
                      .map((payout) => (
                        <div key={payout.id} className="mb-1">
                          {payout.label} {payout.offerPayoutRules?.length && payout.offerPayoutRules?.length > 1 ? '(Multiple)' : ''}
                          {payout.flatPayout && payout.percentPayout
                            ? ` ($${payout.flatPayout} + ${payout.percentPayout}%)`
                            : payout.flatPayout
                              ? ` ($${payout.flatPayout})`
                              : payout.percentPayout
                                ? ` (${payout.percentPayout}%)`
                                : ''}
                        </div>
                      ))}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
        {/* KPI Filters */}
        {numericFilters && Object.keys(numericFilters).some((key) => numericFilters[key]?.length > 0) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Tag
                  label="KPI Filters"
                  bgColor="bg-grey-2"
                  textColor="text-grey-6"
                  onRemove={() => setNumericFilters({})}
                  isRemovable
                  className="cursor-pointer"
                  onClick={() => setIsMemberTableFilterOpen(true)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
              <TooltipHeader>
                <TooltipTitle>KPI Filters</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemKey>Applied Filters</TooltipItemKey>
                  <TooltipItemValue>
                    {Object.entries(numericFilters || {}).map(([columnId, filterValues]) => {
                      if (!filterValues || filterValues.length === 0) return null;
                      const columnLabels: Record<string, string> = {
                        clicks: 'Clicks',
                        conversions: 'Conversions',
                        sales: 'Sales Amount',
                        avgSale: 'Average Sale',
                        payoutEarned: 'Commission Earned',
                        payoutMade: 'Payout Made',
                      };
                      const operatorLabels: Record<string, string> = {
                        gt: '>',
                        lt: '<',
                        gte: '≥',
                        lte: '≤',
                        eq: '=',
                        between: 'between',
                      };
                      const [operator, value] = filterValues;
                      if (!operator || !value) return null;
                      let displayValue = value;
                      if (operator === 'between') {
                        const [min, max] = value.split(',');
                        displayValue = `${min} - ${max}`;
                      }
                      return (
                        <div key={columnId} className="mb-1">
                          {columnLabels[columnId] || columnId} {operatorLabels[operator] || operator} {displayValue}
                        </div>
                      );
                    })}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
        {/* Status Filters */}
        {selectedStatusFilters && selectedStatusFilters.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Tag
                  label={`Status (${selectedStatusFilters.length})`}
                  bgColor="bg-grey-2"
                  textColor="text-grey-6"
                  onRemove={() => setSelectedStatusFilters([])}
                  isRemovable
                  className="cursor-pointer"
                  onClick={() => setIsMemberTableFilterOpen(true)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
              <TooltipHeader>
                <TooltipTitle>Status Filters</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemKey>Selected Statuses</TooltipItemKey>
                  <TooltipItemValue>
                    {selectedStatusFilters.map((status) => (
                      <div key={status} className="mb-1">
                        {status}
                      </div>
                    ))}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
        {/* Boost Type Filters */}
        {selectedBoostTypes && selectedBoostTypes.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Tag
                  label={`Rewards (${selectedBoostTypes.length})`}
                  bgColor="bg-grey-2"
                  textColor="text-grey-6"
                  onRemove={() => setSelectedBoostTypes([])}
                  isRemovable
                  className="cursor-pointer"
                  onClick={() => setIsMemberTableFilterOpen(true)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
              <TooltipHeader>
                <TooltipTitle>Rewards Filter</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemKey>Selected Rewards</TooltipItemKey>
                  <TooltipItemValue>
                    {selectedBoostTypes.map((boostId) => {
                      // Find boost that matches this ID
                      const matchingBoost = effectiveBoostData?.find((boostItem) => boostItem.boost?.id.toString() === boostId);

                      if (matchingBoost) {
                        return (
                          <div key={boostId} className="mb-1">
                            {matchingBoost.boost?.label || boostId}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
        {/* Boost Status Filters */}
        {selectedBoostStatuses && selectedBoostStatuses.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Tag
                  label={`Reward Status (${selectedBoostStatuses.length})`}
                  bgColor="bg-grey-2"
                  textColor="text-grey-6"
                  onRemove={() => setSelectedBoostStatuses([])}
                  isRemovable
                  className="cursor-pointer"
                  onClick={() => setIsMemberTableFilterOpen(true)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
              <TooltipHeader>
                <TooltipTitle>Reward Status Filter</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemKey>Selected Statuses</TooltipItemKey>
                  <TooltipItemValue>
                    {selectedBoostStatuses.map((status) => (
                      <div key={status} className="mb-1">
                        {status === 'PENDING' ? 'Waiting for approval' :
                          status === 'APPROVED' ? 'Claimed' :
                            status === 'REJECTED' ? 'Rejected' :
                              status === 'NOT_REACHED' ? 'Not reached' :
                                status === 'ACTION_NEEDED' ? 'Action Needed' :
                                  status === 'NOT_ELIGIBLE' ? 'Not eligible' : status}
                      </div>
                    ))}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {/* Archived Alert */}
      {archivedDate && (
        <div className="px-4 pt-2">
          <Alert
            message={formatArchivedMessage(archivedDate, archivedUserName)}
            type="warning"
            icon={<BoxArchiveIcon />}
            showIcon
          />
        </div>
      )}
      {/* Migration Alert */}
      {migrateToGraphQL && isOldOffer && (
        <Alert
          message="Upgrade Your Offer: To continue editing or adding members to your offer, please upgrade to the latest sales tracking capabilities. Click 'Upgrade Now' to proceed. "
          type="info"
          showIcon
          action={(
            <Button size="small" type="ghost" onClick={() => setShowMigrationModal(true)}>
              Upgrade Now
            </Button>
          )}
          className="mt-2"
        />
      )}
    </>
  );
};
